/**
 * MemberCollectCharges — Phase 39 grouped "Charges due" collect action for
 * the Member profile. Full-clear ONLY (mirrors MemberCollectPayment's Plan
 * rail precedent, no partial allocation): gathers ALL of this member's
 * collectible non-Plan charges (F&B/Prints/Stationery/Booking/Conference
 * Room — Pending only, SC5) and mounts the shared ChargeRecordPaymentDialog
 * (extracted in 39-03) with the oldest as `charge` and every remaining one as
 * `additionalCharges` — so ONE Collect-charges action clears the member's
 * ENTIRE outstanding non-Plan charges, not just one.
 *
 * This is the SECOND, SEPARATE rail from MemberCollectPayment — never merged
 * with the Plan `Collect`/`Payment due` action (SC4). It has to assemble its
 * selection set in TWO steps because member_charges has no contactId of its
 * own (only accountId — see 39-RESEARCH.md Pitfall 3):
 *   Step A — WHICH charge ids belong to THIS CONTACT: reuse the already-
 *            fetched GET /contacts/:id/transactions (same query key as
 *            MemberTransactionsPanel — React Query dedupes, no extra fetch).
 *   Step B — the full MemberCharge objects (account-scoped; intersect with
 *            the id set from Step A). Uses the SAME shared
 *            pendingChargesParams shape as the panel (Pitfall 4 — avoids a
 *            duplicate cache entry).
 *
 * Plan 09 (gap closure) — SECOND, DISJOINT rail added: `collectTargets(txns)`
 * splits this contact's collectible non-Plan rows into `{ chargeIds,
 * stockOutIds }` (39-07). `chargeIds` still drives Step B above (unchanged
 * charge/additionalCharges assembly). `stockOutIds` are charge-less, unpaid
 * stock_out rows straight from `txns` (no separate account-scoped fetch
 * needed — the row itself carries id/description/amount) and are passed as
 * `stockOutTargets` to the shared dialog alongside `additionalCharges`, so
 * ONE captured payment settles the member's ENTIRE outstanding balance
 * across BOTH rails. A member with real F&B consumption but zero
 * `member_charges` rows (the 39-UAT.md production shape) now still collects:
 * `charge` is left `undefined` and 39-08's no-charge path takes over.
 */
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetContactTransactions,
  useListMemberCharges,
  getGetContactTransactionsQueryKey,
  getListMemberChargesQueryKey,
  type ContactTransactions,
  type ContactTransactionRow,
} from "@workspace/api-client-react";
import { ChargeRecordPaymentDialog } from "@/components/billing/charge-record-payment-dialog";
import { collectTargets } from "@/lib/charge-collection";
import { pendingChargesParams } from "@/components/members/member-transactions-panel";

// Same disjoint bucket set as `collectTargets`/`sumChargesDue` in
// charge-collection.ts — Plan rows are never part of this rail. Kept local
// (rather than exported from charge-collection.ts) since it's only needed
// here to scan `txns` for the stock_out-rail row objects themselves.
const NON_PLAN_BUCKETS: (keyof ContactTransactions)[] = [
  "booking",
  "conferenceRoom",
  "fnb",
  "prints",
  "stationery",
  "addOns",
];

export function MemberCollectCharges({
  accountId,
  contactId,
  open,
  onClose,
}: {
  accountId: number;
  contactId: number;
  /** Only fetches/renders while true — mirrors MemberCollectPayment's
   *  `enabled: open` gating pattern for a conditionally-mounted dialog. */
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  // Step A — this contact's collectible non-Plan ids, split into the two
  // DISJOINT rails (39-07 collectTargets): chargeIds (member_charges, Step B
  // below) and stockOutIds (charge-less, unpaid stock_out rows — settled
  // straight from the row data already in `txns`, no extra fetch).
  const { data: txns } = useGetContactTransactions(contactId, {
    query: { enabled: open, queryKey: getGetContactTransactionsQueryKey(contactId) },
  });
  const targets = useMemo(
    () => collectTargets(txns as ContactTransactions | undefined),
    [txns],
  );

  // Step B — the full MemberCharge rows (account-scoped Pending charges),
  // filtered down to this contact's collectible chargeIds, oldest-first.
  const pendingParams = pendingChargesParams(accountId);
  const { data: chargesData } = useListMemberCharges(pendingParams, {
    query: { enabled: open, queryKey: getListMemberChargesQueryKey(pendingParams) },
  });
  const chargeIdSet = useMemo(() => new Set(targets.chargeIds), [targets]);
  const myPending = useMemo(
    () =>
      (chargesData?.items ?? [])
        .filter((c) => chargeIdSet.has(c.id))
        .sort((a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()),
    [chargesData, chargeIdSet],
  );

  const [primary, ...additionalCharges] = myPending;

  // Plan 09 (gap closure) — the stock_out-rail targets: scan the same
  // NON_PLAN_BUCKETS `collectTargets` scans, keep only rows whose id is in
  // `targets.stockOutIds`, map to the shape ChargeRecordPaymentDialog needs.
  const stockOutTargets = useMemo(() => {
    const t = txns as ContactTransactions | undefined;
    if (!t || targets.stockOutIds.length === 0) return [];
    const idSet = new Set(targets.stockOutIds);
    const out: { id: number; description: string; total: number }[] = [];
    for (const bucket of NON_PLAN_BUCKETS) {
      for (const row of (t[bucket] ?? []) as ContactTransactionRow[]) {
        if (idSet.has(row.id)) {
          out.push({ id: row.id, description: row.description, total: row.amount ?? 0 });
        }
      }
    }
    return out;
  }, [txns, targets]);

  function handleClose() {
    queryClient.invalidateQueries({ queryKey: getGetContactTransactionsQueryKey(contactId) });
    queryClient.invalidateQueries({ queryKey: getListMemberChargesQueryKey(pendingParams) });
    onClose();
  }

  // Empty state — nothing collectible for this member (or not open yet):
  // render nothing rather than an empty/disabled dialog. A pure-stock_out
  // member (no member_charges at all — the real 39-UAT.md F&B shape) still
  // has a truthy `stockOutTargets.length`, so this still opens.
  if (!open || (!primary && stockOutTargets.length === 0)) return null;

  return (
    <ChargeRecordPaymentDialog
      charge={primary}
      additionalCharges={additionalCharges}
      stockOutTargets={stockOutTargets}
      onClose={handleClose}
    />
  );
}
